<template>
  <v-container fluid class="ma-4 mt-3 px-8">
    <!-- STEP 1: Search Student -->
    <v-card outlined class="mb-5">
      <v-card-title class="subtitle-1 font-weight-bold primary--text pb-1">
        <v-icon small class="mr-1" color="primary">mdi-account-search</v-icon>
        Step 1 — Find Student
      </v-card-title>
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="3">
            <v-select
              v-model="searchType"
              :items="searchTypeOptions"
              item-text="label"
              item-value="value"
              label="Search By"
              outlined
              dense
              hide-details="auto"
              @change="onSearchTypeChange"
            />
          </v-col>
          <v-col cols="12" md="5">
            <v-text-field
              v-model.trim="searchQuery"
              :label="searchPlaceholder"
              outlined
              dense
              hide-details="auto"
              clearable
              @keyup.enter="searchStudent"
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-btn
              color="primary"
              :loading="searchLoading"
              :disabled="!searchQuery || searchLoading"
              @click="searchStudent"
            >
              <v-icon left>mdi-magnify</v-icon>
              Search
            </v-btn>
          </v-col>
        </v-row>

        <!-- Search Results -->
        <v-row v-if="searchResults.length > 0" class="mt-3">
          <v-col cols="12">
            <div class="text-caption grey--text mb-2">
              {{ searchResults.length }} result(s) found — select the student to
              transfer
            </div>
            <v-sheet outlined>
              <v-data-table
                :headers="searchHeaders"
                :items="searchResults"
                fixed-header
                hide-default-footer
                :items-per-page="-1"
                class="secondary row-height-50"
                @click:row="selectStudent"
                style="cursor: pointer"
              >
                <template #[`item.select`]="{ item }">
                  <v-btn
                    x-small
                    color="primary"
                    outlined
                    @click.stop="selectStudent(item)"
                  >
                    Select
                  </v-btn>
                </template>
              </v-data-table>
            </v-sheet>
          </v-col>
        </v-row>

        <v-alert
          v-if="searchDone && searchResults.length === 0"
          type="info"
          dense
          outlined
          class="mt-3"
        >
          No student found with the given search criteria.
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- STEP 2: Student Info + Transfer Form -->
    <v-card v-if="selectedStudent" outlined class="mb-5">
      <v-card-title class="subtitle-1 font-weight-bold primary--text pb-1">
        <v-icon small class="mr-1" color="primary">mdi-pencil-box</v-icon>
        Step 2 — Current Details &amp; Transfer Options
      </v-card-title>
      <v-card-text>
        <!-- Current Student Info -->
        <v-sheet rounded outlined class="pa-4 mb-5 grey lighten-5">
          <v-row dense>
            <v-col cols="12" md="3">
              <div class="text-caption grey--text">Name</div>
              <div class="font-weight-medium">{{ selectedStudent.name }}</div>
            </v-col>
            <v-col cols="12" md="3">
              <div class="text-caption grey--text">Email</div>
              <div class="font-weight-medium">{{ selectedStudent.email }}</div>
            </v-col>
            <v-col cols="12" md="2">
              <div class="text-caption grey--text">Mobile</div>
              <div class="font-weight-medium">
                {{ selectedStudent.mobile_no }}
              </div>
            </v-col>
            <v-col cols="12" md="2">
              <div class="text-caption grey--text">Application ID</div>
              <div class="font-weight-medium">
                {{ selectedStudent.application_id || "—" }}
              </div>
            </v-col>
            <v-col cols="12" md="2">
              <div class="text-caption grey--text">Status</div>
              <div class="font-weight-medium">
                {{ selectedStudent.application_status || "—" }}
              </div>
            </v-col>
            <v-col cols="12" md="2">
              <div class="text-caption grey--text">USN</div>
              <div class="font-weight-medium">
                {{ selectedStudent.usn_id || "—" }}
              </div>
            </v-col>
            <v-col cols="12" md="2">
              <div class="text-caption grey--text">Current Batch</div>
              <div class="font-weight-medium">
                {{ selectedStudent.year_batch }}
              </div>
            </v-col>
            <v-col cols="12" md="3">
              <div class="text-caption grey--text">Current Program</div>
              <div class="font-weight-medium">
                {{ selectedStudent.program_name }}
              </div>
            </v-col>
            <v-col cols="12" md="3">
              <div class="text-caption grey--text">Current Partner</div>
              <div class="font-weight-medium">
                {{ selectedStudent.partner_name }}
              </div>
            </v-col>
          </v-row>
        </v-sheet>

        <!-- Transfer Form -->
        <v-form ref="transferForm">
          <div
            class="text-caption font-weight-bold grey--text text--darken-2 mb-3 text-uppercase"
          >
            New Assignment
          </div>
          <v-row>
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="transferForm.program"
                :items="programs"
                :item-text="
                  (p) =>
                    `${p.degree.short_name} ${
                      p.specialization ? '- ' + p.specialization : ''
                    }`
                "
                item-value="id"
                return-object
                label="New Program *"
                outlined
                dense
                hide-details="auto"
                :rules="[rules.required]"
                @change="onProgramChange"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="transferForm.partner"
                :items="partners"
                item-text="name"
                item-value="id"
                return-object
                label="New Partner *"
                outlined
                dense
                hide-details="auto"
                :rules="[rules.required]"
                @change="onPartnerChange"
              />
            </v-col>
            <v-col cols="12" md="2">
              <v-text-field
                :value="currentYearBatch"
                label="Year - Batch"
                outlined
                dense
                hide-details="auto"
                readonly
              />
            </v-col>
            <v-col cols="12" md="2" class="d-flex align-center">
              <v-btn
                color="primary"
                outlined
                :loading="lookupLoading"
                :disabled="
                  !transferForm.program ||
                  !transferForm.partner ||
                  !currentYearBatch
                "
                @click="lookupProgramBatch"
              >
                <v-icon left small>mdi-database-search</v-icon>
                Lookup
              </v-btn>
            </v-col>
          </v-row>

          <v-alert v-if="lookupError" type="error" dense outlined class="mt-2">
            {{ lookupError }}
          </v-alert>

          <!-- Fee Structure Preview -->
          <div v-if="resolvedFeeStructure" class="mt-4">
            <div
              class="text-caption font-weight-bold grey--text text--darken-2 mb-2 text-uppercase"
            >
              Fee Structure (New Assignment)
            </div>
            <v-sheet rounded outlined class="pa-4 grey lighten-5">
              <v-row dense>
                <v-col
                  cols="6"
                  md="2"
                  v-for="(val, key) in feeDisplayFields"
                  :key="key"
                >
                  <div class="text-caption grey--text">{{ val.label }}</div>
                  <div class="font-weight-medium">
                    ₹ {{ resolvedFeeStructure[val.field] || "0" }}
                  </div>
                </v-col>
              </v-row>
            </v-sheet>
          </div>

          <!-- Admit Plan Update Options (only if student has an application_id) -->
          <div
            v-if="selectedStudent.application_id && resolvedProgramBatch"
            class="mt-4"
          >
            <div
              class="text-caption font-weight-bold grey--text text--darken-2 mb-2 text-uppercase"
            >
              Admit Plan Update
            </div>
            <v-sheet rounded outlined class="pa-4 grey lighten-5">
              <v-row dense>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="transferForm.plan_selected"
                    :items="planOptions"
                    label="Plan Selected"
                    outlined
                    dense
                    hide-details="auto"
                  />
                </v-col>
                <!-- <v-col cols="12" md="4">
                  <v-select
                    v-model="transferForm.option_selected"
                    :items="optionOptions"
                    label="Option Selected"
                    outlined
                    dense
                    hide-details="auto"
                  />
                </v-col> -->
              </v-row>
              <div class="text-caption grey--text mt-2">
                Admission fee, advance paid, and plan amounts will be
                recalculated from the new fee structure.
              </div>
            </v-sheet>
          </div>

          <!-- Remarks -->
          <v-row class="mt-3">
            <v-col cols="12" md="8">
              <v-textarea
                v-model="transferForm.remarks"
                label="Reason / Remarks *"
                outlined
                dense
                rows="2"
                hide-details="auto"
                :rules="[rules.required]"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn text color="error" @click="resetAll">Cancel</v-btn>
        <v-btn
          color="primary"
          :loading="saveLoading"
          :disabled="!resolvedProgramBatch || !hasChanges || saveLoading"
          @click="confirmTransfer"
        >
          <v-icon left>mdi-content-save</v-icon>
          Apply Transfer
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Confirm Dialog -->
    <v-dialog v-model="confirmDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="headline font-weight-bold">
          <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
          Confirm Transfer
        </v-card-title>
        <v-card-text>
          <p>
            You are about to transfer
            <strong>{{ selectedStudent && selectedStudent.name }}</strong> to:
          </p>
          <v-list dense>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  Year - Batch: <strong>{{ currentYearBatch }}</strong>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  Program:
                  <strong>{{ programDisplayName }}</strong>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  Partner:
                  <strong>{{
                    transferForm.partner && transferForm.partner.name
                  }}</strong>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <!-- Outcome comes from the backend preview (same rules as apply) -->
          <div v-if="previewLoading" class="text-center py-3">
            <v-progress-circular indeterminate size="24" color="primary" />
          </div>
          <template v-else-if="preview">
            <v-row dense class="mb-1">
              <v-col cols="6">
                <div class="text-caption grey--text">Status after transfer</div>
                <div class="font-weight-medium">
                  {{ preview.current_status }} → {{ preview.new_status }}
                </div>
              </v-col>
              <v-col v-if="preview.plan_amount_balance !== null" cols="6">
                <div class="text-caption grey--text">Balance under new fee</div>
                <div class="font-weight-medium">
                  ₹ {{ preview.plan_amount_balance }}
                </div>
              </v-col>
              <v-col v-if="preview.excess_amount" cols="6">
                <div class="text-caption grey--text">Tuition fee excess</div>
                <div class="font-weight-medium">₹ {{ preview.excess_amount }}</div>
              </v-col>
            </v-row>
            <v-alert :type="preview.severity" dense text class="mt-2 mb-2">
              {{ preview.message }}
            </v-alert>
            <v-checkbox
              v-if="preview.requires_ack"
              v-model="usnResetAcknowledged"
              dense
              hide-details="auto"
              label="Yes, I understand and want to proceed — this cannot be undone."
            />
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="error" @click="confirmDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="primary"
            :loading="saveLoading"
            :disabled="
              !preview ||
              !preview.can_proceed ||
              (preview.requires_ack && !usnResetAcknowledged)
            "
            @click="applyTransfer"
          >
            Yes, Transfer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { studentTransferHeaders } from "@/constants/headers";

export default {
  name: "StudentTransfer",

  data() {
    return {
      // Search
      searchType: "application_id",
      searchQuery: "",
      searchLoading: false,
      searchDone: false,
      searchResults: [],
      searchTypeOptions: [
        { label: "Application ID", value: "application_id" },
        { label: "Name", value: "name" },
        { label: "Email", value: "email" },
        { label: "Mobile No", value: "mobile_no" },
      ],
      searchHeaders: studentTransferHeaders,

      // Selected student
      selectedStudent: null,

      // Reference data
      programs: [],
      partners: [],
      programBatches: [], // raw from /program_batches
      currentYearBatch: null, // latest unique year_batch string e.g. "2026-1"

      // Transfer form
      transferForm: {
        program: null,
        partner: null,
        year: null,
        batch: null,
        plan_selected: null,
        option_selected: null,
        remarks: "",
      },
      planOptions: ["Term Fee", "Annual", "Full"],
      optionOptions: ["Direct", "Loan", "Installment"],

      // Resolved batch/fee
      resolvedProgramBatch: null,
      resolvedFeeStructure: null,
      lookupLoading: false,
      lookupError: null,

      feeDisplayFields: [
        { label: "Application Fee", field: "application_fee" },
        { label: "Advance Fee", field: "advance_fee" },
        { label: "Admission Fee", field: "admission_fee" },
        { label: "Course Fee", field: "course_fee" },
        { label: "Term Fee", field: "term_fee" },
        { label: "Annual Upfront", field: "annual_upfront" },
        { label: "Full Upfront", field: "full_upfront" },
      ],

      // Save
      saveLoading: false,
      confirmDialog: false,
      usnResetAcknowledged: false,
      preview: null,
      previewLoading: false,

      rules: {
        required: (v) => !!v || "Required",
      },
    };
  },

  computed: {
    searchPlaceholder() {
      const map = {
        name: "Enter student name",
        email: "Enter email address",
        mobile_no: "Enter mobile number",
        application_id: "Enter application ID",
      };
      return map[this.searchType] || "Search...";
    },

    programDisplayName() {
      const p = this.transferForm.program;
      if (!p) return "—";
      const degree = p.degree?.short_name || "";
      const spec = p.specialization ? ` - ${p.specialization}` : "";
      return `${degree}${spec}`;
    },

    hasChanges() {
      if (!this.selectedStudent || !this.transferForm.program) return false;
      const programChanged =
        this.transferForm.program?.id !== this.selectedStudent.program_id;
      const partnerChanged =
        this.transferForm.partner?.id !== this.selectedStudent.partner_id;
      // Year & batch are always the current active batch — not user-editable.
      // A student on an older batch is always "changing" to the current batch,
      // so we check if they differ from the student's existing year/batch too.
      const yearChanged = this.transferForm.year !== this.selectedStudent.year;
      const batchChanged =
        this.transferForm.batch !== this.selectedStudent.batch;
      return programChanged || partnerChanged || yearChanged || batchChanged;
    },
  },

  async created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Batch Transfer" }]);
    await Promise.all([
      this.getPrograms(),
      this.getPartners(),
      this.getProgramBatches(),
    ]);
  },

  methods: {
    onSearchTypeChange() {
      this.searchQuery = "";
      this.searchResults = [];
      this.searchDone = false;
    },

    async searchStudent() {
      if (!this.searchQuery) return;
      this.searchLoading = true;
      this.searchDone = false;
      this.searchResults = [];
      this.selectedStudent = null;
      this.resetTransferForm();

      try {
        const res = await this.$axios.get("/student_transfer/search", {
          params: { [this.searchType]: this.searchQuery },
        });
        this.searchResults = res.data.data;
      } catch (err) {
        console.error(err);
        this.$store.commit("showSnackbar", {
          message: "Search failed",
          color: "error",
        });
      } finally {
        this.searchLoading = false;
        this.searchDone = true;
      }
    },

    selectStudent(student) {
      this.selectedStudent = student;
      this.resetTransferForm();

      // Pre-fill program & partner from student's current values
      this.transferForm.program =
        this.programs.find((p) => p.id === student.program_id) || null;
      this.transferForm.partner =
        this.partners.find((p) => p.id === student.partner_id) || null;

      // Year & batch are always locked to the current active batch,
      // not the student's old batch — transfer must go to current batch only
      const [year, batch] = this.currentYearBatch
        ? this.currentYearBatch.split("-").map(Number)
        : [null, null];
      this.transferForm.year = year;
      this.transferForm.batch = batch;
    },

    resetTransferForm() {
      this.transferForm = {
        program: null,
        partner: null,
        year: null,
        batch: null,
        plan_selected: null,
        option_selected: null,
        remarks: "",
      };
      this.resolvedProgramBatch = null;
      this.resolvedFeeStructure = null;
      this.lookupError = null;
    },

    onProgramChange() {
      this.resolvedProgramBatch = null;
      this.resolvedFeeStructure = null;
      this.lookupError = null;
    },

    onPartnerChange() {
      this.resolvedProgramBatch = null;
      this.resolvedFeeStructure = null;
      this.lookupError = null;
    },

    async lookupProgramBatch() {
      this.lookupLoading = true;
      this.lookupError = null;
      this.resolvedProgramBatch = null;
      this.resolvedFeeStructure = null;

      try {
        const res = await this.$axios.get("/student_transfer/lookup_batch", {
          params: {
            program_id: this.transferForm.program.id,
            partner_id: this.transferForm.partner.id,
            year: this.transferForm.year,
            batch: this.transferForm.batch,
          },
        });
        this.resolvedProgramBatch = res.data.data.program_batch;
        this.resolvedFeeStructure = res.data.data.fee_structure;
      } catch (err) {
        this.lookupError =
          err.response?.data?.message ||
          "No program batch / schedule / fee structure found for the selected combination.";
      } finally {
        this.lookupLoading = false;
      }
    },

    transferPayload() {
      return {
        source_id: this.selectedStudent.id,
        application_id: this.selectedStudent.application_id,
        program_id: this.transferForm.program.id,
        partner_id: this.transferForm.partner.id,
        year: this.transferForm.year,
        batch: this.transferForm.batch,
        program_batch_id: this.resolvedProgramBatch.program_batch_id,
        batch_partner_schedule_id:
          this.resolvedProgramBatch.batch_partner_schedule_id,
        batch_partner_course_fee_id:
          this.resolvedProgramBatch.batch_partner_course_fee_id,
        plan_selected: this.transferForm.plan_selected,
        option_selected: this.transferForm.option_selected,
        remarks: this.transferForm.remarks,
      };
    },

    // The dialog message/checkbox come from the backend preview so they always
    // match what apply will actually do (Admitted re-admit, new USN, excess...).
    async confirmTransfer() {
      if (!this.$refs.transferForm.validate()) return;
      this.usnResetAcknowledged = false;
      this.preview = null;
      this.confirmDialog = true;
      this.previewLoading = true;
      try {
        const res = await this.$axios.get("/student_transfer/preview", {
          params: this.transferPayload(),
        });
        this.preview = res.data.data;
      } catch (error) {
        this.confirmDialog = false;
        const errorMessage = await this.$store.dispatch(
          "throwError",
          error.response,
        );
        this.$store.commit("showSnackbar", {
          message: errorMessage,
          color: "error",
        });
      } finally {
        this.previewLoading = false;
      }
    },

    async applyTransfer() {
      this.saveLoading = true;
      try {
        await this.$axios.put("/student_transfer/apply", {
          ...this.transferPayload(),
          confirmed: true,
        });

        this.$store.commit("showSnackbar", {
          message: "Student transferred successfully",
          color: "success",
        });

        this.confirmDialog = false;
        this.resetAll();
      } catch (error) {
        const errorMessage = await this.$store.dispatch(
          "throwError",
          error.response,
        );
        this.$store.commit("showSnackbar", {
          message: errorMessage,
          color: "error",
        });
      } finally {
        this.saveLoading = false;
      }
    },

    resetAll() {
      this.selectedStudent = null;
      this.searchResults = [];
      this.searchQuery = "";
      this.searchDone = false;
      this.confirmDialog = false;
      this.usnResetAcknowledged = false;
      this.preview = null;
      this.resetTransferForm();
    },

    async getPrograms() {
      try {
        const res = await this.$axios.get("/programs");
        this.programs = res.data.data;
      } catch (e) {
        console.error(e);
      }
    },

    async getPartners() {
      try {
        const res = await this.$axios.get("/partners");
        this.partners = res.data.data;
      } catch (e) {
        console.error(e);
      }
    },

    async getProgramBatches() {
      try {
        const res = await this.$axios.get("/program_batches");
        this.programBatches = res.data.data;

        // Deduplicate by year_batch, then pick the latest one
        const unique = [];
        const seen = new Set();
        for (const pb of this.programBatches) {
          if (!seen.has(pb.year_batch)) {
            seen.add(pb.year_batch);
            unique.push(pb);
          }
        }
        // Sort descending by year then batch number, take the first
        unique.sort((a, b) => {
          if (b.year !== a.year) return b.year - a.year;
          const batchA = parseInt(a.year_batch.split("-")[1]) || 0;
          const batchB = parseInt(b.year_batch.split("-")[1]) || 0;
          return batchB - batchA;
        });
        this.currentYearBatch = unique.length ? unique[0].year_batch : null;

        // Seed transferForm with the active year/batch immediately
        if (this.currentYearBatch) {
          const [year, batch] = this.currentYearBatch.split("-").map(Number);
          this.transferForm.year = year;
          this.transferForm.batch = batch;
        }
      } catch (e) {
        console.error(e);
      }
    },
  },
};
</script>

<style scoped>
.v-data-table::v-deep tbody tr:hover {
  background-color: #e3f2fd !important;
}
.v-data-table::v-deep td {
  font-size: 13px !important;
}
</style>
